import PolygonBase from './PolygonBase';
import shapeCloud from '../assets/icons/shapeCloud.svg';
import { svgToIcon } from '../utils/svg';
import type Shape from '../objects/Shape';
import { AttachmentPoint } from '../types';

const HEIGHT_FACTOR = 0.6;

class ShapeCloud extends PolygonBase {
  constructor(fabricObject: Shape) {
    super(fabricObject);
    this.fabricObject.uniformScaling = true;
  }

  changedToShape() {
    super.changedToShape();

    // Force the cloud into it's default size
    this.fabricObject.set('scaleX', this.fabricObject.scaleY);
    this.fabricObject.set('width', this.fabricObject.height / HEIGHT_FACTOR);
  }

  static renderIcon() {
    return svgToIcon(shapeCloud);
  }

  static defaultHeight() {
    return this.defaultWidth() * HEIGHT_FACTOR;
  }

  static shapeControls = this.createDefaultControls();

  /**
   * Left, right, and top position shifted to align with cloud edges
   */
  static outboundConnectControlDefinitions() {
    return {
      [AttachmentPoint.Left]: {
        x: -0.5,
        y: 0.2,
      },
      [AttachmentPoint.Right]: {
        x: 0.5,
        y: 0.2,
      },
      [AttachmentPoint.Top]: {
        x: 0,
        y: -0.48,
      },
      [AttachmentPoint.Bottom]: {
        x: 0,
        y: 0.5,
      },
    };
  }

  /**
   * Left, right, and top position shifted to align with cloud edges
   */
  static connectControlDefinition() {
    return {
      ...super.connectControlDefinition(),
      [AttachmentPoint.Left]: {
        x: -0.5,
        y: 0.2,
        normal: { x: -1, y: 0 },
        oppositeAttachmentPoint: AttachmentPoint.Right,
      },
      [AttachmentPoint.Right]: {
        x: 0.5,
        y: 0.2,
        normal: { x: 1, y: 0 },
        oppositeAttachmentPoint: AttachmentPoint.Left,
      },
      [AttachmentPoint.Top]: {
        x: 0,
        y: -0.48,
        normal: { x: 0, y: -1 },
        oppositeAttachmentPoint: AttachmentPoint.Bottom,
      },
      [AttachmentPoint.Bottom]: {
        x: 0,
        y: 0.5,
        normal: { x: 0, y: 1 },
        oppositeAttachmentPoint: AttachmentPoint.Top,
      },
    };
  }

  /**
   * Source SVG of path:
   *
   * To modify the shape, use this SVG. Ensure the SVG path stays within a 1x1 box, keeping the viewport as specified below.
   *
   * <svg width="1mm" height="1mm" viewBox="-0.5 -0.5 1 1" id="svg5" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg"><path style="stroke:#000000;fill:none;stroke-opacity:1;stroke-width:0.00037795;stroke-miterlimit:4;stroke-dasharray:none;vector-effect:non-scaling-stroke;-inkscape-stroke:hairline" d="M -0.337792,0.4987522 C -0.42668786,0.4987522 -0.49875221,0.3808764 -0.49875221,0.23547223 C -0.49875221,0.11056798 -0.44555622,0.0060519 -0.37421046,-0.02095276 C -0.37277127,-0.28523513 -0.2413404,-0.49875221 -0.07941971,-0.49875221 C 0.03174759,-0.49875221 0.12852601,-0.39810228 0.17875768,-0.24948744 C 0.19404405,-0.26030865 0.21088756,-0.26633304 0.22860257,-0.26633304 C 0.29240658,-0.26633304 0.34506587,-0.18851973 0.35312833,-0.08774638 C 0.43615252,-0.06081677 0.49875219,0.05894373 0.49875219,0.20265 C 0.49875219,0.36618237 0.41770291,0.49874885 0.3177263,0.49874885 C 0.28987901,0.49874885 -0.31871175,0.49874885 -0.337791,0.49874885 Z" id="path651" /></svg>
   */
  static pathString() {
    return `
      M -0.337792,0.4987522
      C -0.42668786,0.4987522 -0.49875221,0.3808764 -0.49875221,0.23547223
      C -0.49875221,0.11056798 -0.44555622,0.0060519 -0.37421046,-0.02095276
      C -0.37277127,-0.28523513 -0.2413404,-0.49875221 -0.07941971,-0.49875221
      C 0.03174759,-0.49875221 0.12852601,-0.39810228 0.17875768,-0.24948744
      C 0.19404405,-0.26030865 0.21088756,-0.26633304 0.22860257,-0.26633304
      C 0.29240658,-0.26633304 0.34506587,-0.18851973 0.35312833,-0.08774638
      C 0.43615252,-0.06081677 0.49875219,0.05894373 0.49875219,0.20265
      C 0.49875219,0.36618237 0.41770291,0.49874885 0.3177263,0.49874885
      C 0.28987901,0.49874885 -0.31871175,0.49874885 -0.337791,0.49874885"
      Z`;
  }
}

export default ShapeCloud;
